import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  registrationContext,
  type Registrations,
} from '~/app/features/registrations/registrationMiddleware';
import { settingsContext, type Settings } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { draftRegistrationToCreate } from '~/utils/registration';
import { makeAction, makeLoader, redirect } from '~/utils/routing';

export const SEARCH_PARAM = 'q';

const pickProductTheme = (theme: Settings['theme']) => ({
  identifyProductUrl: theme.identifyProductUrl,
  identifyProductFormPlaceholder:
    theme.identifyProductFormPlaceholder ?? 'Search for your product',
  identifyProductUrlButtonText:
    theme.identifyProductUrlButtonText ?? 'Identify Your Product',
  identifyProductDetailText:
    theme.identifyProductDetailText ??
    'If you need help identifying your product, please contact us.',
});

const searchProducts = ({
  draft,
  storefrontId,
  request,
}: {
  draft: NonNullable<Registrations['draft']>;
  storefrontId: Settings['storefrontId'];
  request: Request;
}) => {
  const searchTerm = new URLSearchParams(new URL(request.url).search).get(
    SEARCH_PARAM,
  );

  if (!searchTerm || !draft.storeSalesChannel) return Promise.resolve([]);

  return api.fetchRegistrationProducts({
    params: { storefrontId },
    query: {
      searchTerm,
      storeSalesChannelName: draft.storeSalesChannel.name,
      estimatedPurchaseDate: draft.estimatedPurchaseDate,
    },
  });
};

export const { loader, useLoaderData } = makeLoader(
  ({ context, params, request }) => {
    const { storefrontId, theme } = context.get(settingsContext);
    const { draft, suggested } = context.get(registrationContext);

    return !draft
      ? redirect(`/${params.store ?? ''}`)
      : searchProducts({ draft, storefrontId, request }).then((products) => ({
          products,
          ...pickProductTheme(theme),
          suggestedVariants:
            suggested?.productMatchMetadata?.suggestedVariants ?? [],
          possibleVariants:
            suggested?.productMatchMetadata?.possibleVariants ?? [],
        }));
  },
);

export const FORM_KEY = 'variants';
const formSchema = zfd.formData({
  [FORM_KEY]: zfd.repeatable(
    z
      .array(
        z.object({
          id: zfd.numeric(),
          quantity: zfd.numeric(z.number().min(1)),
        }),
      )
      .min(1),
  ),
});

export const { action, useActionResult } = makeAction(
  ({ context, formData, params }, appStore) => {
    const settings = context.get(settingsContext);
    const registrations = context.get(registrationContext);

    return match({
      draft: registrations.draft,
      formDataResult: formSchema.safeParse(formData),
    })
      .with({ draft: P.nullish }, () => redirect(`/${params.store ?? ''}`))
      .with(
        { formDataResult: { success: false } },
        () =>
          ({
            ok: false,
            message: 'Invalid form data',
          }) as const,
      )
      .with(
        { draft: P.not(P.nullish), formDataResult: { success: true } },
        ({ draft, formDataResult }) =>
          api
            .createRegistration({
              params: { storefrontId: settings.storefrontId },
              body: draftRegistrationToCreate({
                draft,
                suggested: registrations.suggested,
                referrer: registrations.referrer,
                storeProductVariants: formDataResult.data[FORM_KEY],
              }),
            })
            .then((registration) => {
              appStore.dispatch(
                appStore.actions.registrations.setRegistration(registration),
              );

              return redirect(`../overview`);
            }),
      )
      .otherwise(() => {
        throw new Error('Unexpected state in selectProducts action');
      });
  },
);
